import { IconLoader2 } from '@tabler/icons-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  getSimSwapVerificationFlowLabel,
  getSimSwapVerificationRequirement,
  getSimSwapVerificationStatusLabel,
  isSimSwapVerificationCurrent,
  type SimSwapVerificationState,
} from './simSwapReadiness';

type SimSwapVerificationPanelProps = {
  verification?: SimSwapVerificationState | null;
  blockers?: string[];
  loading?: boolean;
  formatDate: (value?: string) => string;
};

export function SimSwapVerificationPanel({
  verification,
  blockers = [],
  loading = false,
  formatDate,
}: SimSwapVerificationPanelProps) {
  const status = verification?.status ?? null;
  const verificationCurrent = isSimSwapVerificationCurrent(verification);
  const statusLabel =
    status === 'PASSED' && !verificationCurrent
      ? 'Expired or incomplete'
      : getSimSwapVerificationStatusLabel(status);
  const statusVariant = verificationCurrent
    ? 'success'
    : status === 'PENDING'
      ? 'warning'
      : 'secondary';

  return (
    <div className="rounded-md border bg-muted/20 p-3 text-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="font-semibold">Current SIM swap verification</p>
          <p className="mt-1 text-muted-foreground">
            {verification?.initiationUnavailableReason ||
              getSimSwapVerificationRequirement(verification?.flow)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {loading && (
            <Badge variant="outline">
              <IconLoader2 className="mr-1 size-3 animate-spin" />
              Loading…
            </Badge>
          )}
          <Badge variant={statusVariant as any}>{statusLabel}</Badge>
        </div>
      </div>
      <dl className="mt-3 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-[auto_1fr]">
        <dt className="font-medium">Verification path</dt>
        <dd>{getSimSwapVerificationFlowLabel(verification?.flow)}</dd>
        <dt className="font-medium">Verified</dt>
        <dd>
          {verification?.verifiedAt
            ? formatDate(verification.verifiedAt)
            : 'Not completed'}
        </dd>
        <dt className="font-medium">Expires</dt>
        <dd>
          {verification?.expiresAt
            ? formatDate(verification.expiresAt)
            : 'No current verification expiry'}
        </dd>
      </dl>
      {blockers.length > 0 && (
        <Alert variant="destructive" className="mt-3">
          <AlertDescription>
            <ul className="list-disc space-y-1 pl-4">
              {blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
